import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
    Check,
    ChevronDown,
    ChevronUp,
    FileText,
    Loader2,
    X,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { crud, whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

function formatTime(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function WhatsAppInboxOnayDrawer({
    open,
    onClose,
    initialInboxId = null,
    onDone,
}) {
    const [kuyruk, setKuyruk] = useState([]);
    const [loading, setLoading] = useState(false);
    const [seciliId, setSeciliId] = useState(null);
    const [onizleme, setOnizleme] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [dosyaArama, setDosyaArama] = useState("");
    const [dosyaAdaylar, setDosyaAdaylar] = useState([]);
    const [seciliDosyaId, setSeciliDosyaId] = useState("");
    const [konusmaKaydet, setKonusmaKaydet] = useState(true);
    const [herZamanBuDosya, setHerZamanBuDosya] = useState(false);
    const [busy, setBusy] = useState(false);

    const seciliKayit = useMemo(
        () => kuyruk.find((k) => k.inbox_id === seciliId) || null,
        [kuyruk, seciliId],
    );

    const loadKuyruk = useCallback(async () => {
        setLoading(true);
        try {
            const data = await whatsappMerkez.inboxOnayKuyrugu({ limit: 100 });
            const rows = data.kuyruk || [];
            setKuyruk(rows);
            setSeciliId((prev) => {
                if (prev && rows.some((r) => r.inbox_id === prev)) return prev;
                if (initialInboxId && rows.some((r) => r.inbox_id === initialInboxId)) {
                    return initialInboxId;
                }
                return rows[0]?.inbox_id || null;
            });
        } catch {
            toast.error("Onay kuyruğu yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [initialInboxId]);

    useEffect(() => {
        if (!open) return;
        loadKuyruk();
    }, [open, loadKuyruk]);

    useEffect(() => {
        if (!open || !seciliId) {
            setOnizleme(null);
            setPreviewUrl("");
            return;
        }
        let cancelled = false;
        let objectUrl = "";

        (async () => {
            try {
                const data = await whatsappMerkez.inboxEslestirmeOnizle(seciliId);
                if (cancelled) return;
                setOnizleme(data);
                const es = data.eslestirme || {};
                setSeciliDosyaId(String(es.dosya_id || ""));
                setDosyaArama(es.dosya_no || es.plaka || "");
                if (data.bekleyen_dosya_var) {
                    const blob = await whatsappMerkez.inboxBekleyenMedyaBlob(seciliId);
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(blob);
                    setPreviewUrl(objectUrl);
                } else {
                    setPreviewUrl("");
                }
            } catch {
                if (!cancelled) toast.error("Önizleme yüklenemedi.");
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, seciliId]);

    const es = onizleme?.eslestirme || seciliKayit?.eslestirme || {};
    const hedefDosyaId = seciliDosyaId || (es.dosya_id ? String(es.dosya_id) : "");

    const handleOnayla = useCallback(async () => {
        if (!seciliId || busy) return;
        const dosyaId = seciliDosyaId || (es.dosya_id ? String(es.dosya_id) : "");
        if (!dosyaId) {
            toast.error("Hedef dosya seçin.");
            return;
        }
        setBusy(true);
        try {
            const res = await whatsappMerkez.inboxOnayla(seciliId, {
                dosya_id: Number(dosyaId),
                konusma_kaydet: konusmaKaydet,
                her_zaman_bu_dosya: herZamanBuDosya,
            });
            if (res?.ok) {
                toast.success(res.detail || "Evrak onaylandı ve yüklendi.");
                await loadKuyruk();
                onDone?.();
            } else {
                toast.error(res?.detail || "Onay başarısız.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Onay hatası.");
        } finally {
            setBusy(false);
        }
    }, [
        seciliId,
        busy,
        seciliDosyaId,
        es.dosya_id,
        konusmaKaydet,
        herZamanBuDosya,
        loadKuyruk,
        onDone,
    ]);

    useEffect(() => {
        if (!open) return;
        function onKey(e) {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
                return;
            }
            if (!(e.ctrlKey || e.metaKey)) return;
            if (e.key === "Enter") {
                e.preventDefault();
                handleOnayla();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose, handleOnayla]);

    async function handleReddet() {
        if (!seciliId || busy) return;
        setBusy(true);
        try {
            await whatsappMerkez.inboxReddet(seciliId, "Panel onay çekmecesinden reddedildi.");
            toast.info("Evrak reddedildi.");
            await loadKuyruk();
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reddetme hatası.");
        } finally {
            setBusy(false);
        }
    }

    function selectPrev() {
        if (!kuyruk.length || !seciliId) return;
        const idx = kuyruk.findIndex((k) => k.inbox_id === seciliId);
        const next = kuyruk[Math.max(0, idx - 1)];
        if (next) setSeciliId(next.inbox_id);
    }

    function selectNext() {
        if (!kuyruk.length || !seciliId) return;
        const idx = kuyruk.findIndex((k) => k.inbox_id === seciliId);
        const next = kuyruk[Math.min(kuyruk.length - 1, idx + 1)];
        if (next) setSeciliId(next.inbox_id);
    }

    if (!open) return null;

    return createPortal(
        <div className="wa-onay-drawer" role="dialog" aria-label="Evrak onay kuyruğu">
            <button type="button" className="wa-onay-drawer__backdrop" onClick={onClose} aria-label="Kapat" />
            <aside className="wa-onay-drawer__panel">
                <header className="wa-onay-drawer__header">
                    <div>
                        <h3>Evrak onay kuyruğu</h3>
                        <p>{kuyruk.length} bekleyen · Ctrl+Enter onayla · Esc kapat</p>
                    </div>
                    <button type="button" onClick={onClose} className="wa-onay-drawer__close">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="wa-onay-drawer__body">
                    <div className="wa-onay-drawer__list">
                        {loading ? (
                            <div className="wa-onay-drawer__empty">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : kuyruk.length === 0 ? (
                            <div className="wa-onay-drawer__empty">Onay bekleyen evrak yok.</div>
                        ) : (
                            kuyruk.map((item) => (
                                <button
                                    key={item.inbox_id}
                                    type="button"
                                    className={`wa-onay-item ${seciliId === item.inbox_id ? "wa-onay-item--active" : ""}`}
                                    onClick={() => setSeciliId(item.inbox_id)}
                                >
                                    <FileText className="h-4 w-4 shrink-0 text-sky-300" />
                                    <div className="min-w-0 flex-1 text-left">
                                        <div className="truncate text-sm font-medium">
                                            {item.eslestirme?.dosya_no || item.eslestirme?.muvekkil_adi || "Belirsiz"}
                                        </div>
                                        <div className="truncate text-[11px] text-[#8696a0]">
                                            {item.eslestirme?.muvekkil_adi || item.telefon} · güven %
                                            {item.eslestirme?.guven_puani ?? "?"}
                                        </div>
                                        <div className="text-[10px] text-[#667781]">{formatTime(item.olusturma_tarihi)}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    <div className="wa-onay-drawer__detail">
                        {!seciliKayit ? (
                            <div className="wa-onay-drawer__empty">Soldan bir kayıt seçin.</div>
                        ) : (
                            <>
                                <div className="wa-onay-drawer__nav">
                                    <button type="button" onClick={selectPrev} title="Önceki">
                                        <ChevronUp className="h-4 w-4" />
                                    </button>
                                    <button type="button" onClick={selectNext} title="Sonraki">
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                </div>

                                <div className="wa-onay-drawer__preview">
                                    {previewUrl ? (
                                        (() => {
                                            const fn = (onizleme?.filename || onizleme?.eslestirme?.filename || "").toLowerCase();
                                            const isPdf = fn.endsWith(".pdf");
                                            const isImg = /\.(jpe?g|png|webp|gif|bmp)$/.test(fn) || seciliKayit.media_turu === "image";
                                            if (isPdf) {
                                                return (
                                                    <iframe title="Belge önizleme" src={previewUrl} className="wa-onay-drawer__frame rounded-lg bg-white" />
                                                );
                                            }
                                            if (isImg) {
                                                return (
                                                    <img src={previewUrl} alt="Evrak önizleme" className="wa-onay-drawer__image rounded-lg object-contain" />
                                                );
                                            }
                                            return (
                                                <div className="wa-onay-drawer__file-fallback text-center py-6 text-sm text-[#8696a0]">
                                                    <FileText className="h-10 w-10 mx-auto mb-2 text-sky-300" />
                                                    <div>{onizleme?.filename || "Belge dosyası"}</div>
                                                    <div className="text-xs mt-1">Word/Excel — indirip açın veya OCR metnine bakın</div>
                                                    <a href={previewUrl} download className="text-sky-300 underline text-xs mt-2 inline-block">İndir</a>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <div className="text-sm text-[#8696a0] text-center py-8">Önizleme yok</div>
                                    )}
                                </div>

                                <div className="wa-onay-drawer__meta">
                                    <div>
                                        <span className="label">Müvekkil</span>
                                        <strong>{es.muvekkil_adi || "—"}</strong>
                                    </div>
                                    <div>
                                        <span className="label">Dosya</span>
                                        <strong>{es.dosya_no || "—"}</strong>
                                    </div>
                                    <div>
                                        <span className="label">Plaka</span>
                                        <strong>{es.plaka || "—"}</strong>
                                    </div>
                                    <div>
                                        <span className="label">Güven</span>
                                        <strong>%{es.guven_puani ?? "?"}</strong>
                                    </div>
                                    {onizleme?.eksik_eslesme ? (
                                        <div className="col-span-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                                            Eksik evrak eşleşmesi: <strong>{onizleme.eksik_eslesme.ad}</strong>
                                        </div>
                                    ) : null}
                                    {onizleme?.eksik_evrak_list?.length ? (
                                        <div className="col-span-2">
                                            <span className="label">Eksik evraklar</span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {onizleme.eksik_evrak_list.map((e) => (
                                                    <span key={e.kod} className="wa-onay-eksik-chip">{e.ad}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="col-span-2">
                                        <span className="label">Not</span>
                                        <p className="text-xs text-[#8696a0] mt-0.5">
                                            {onizleme?.notu || seciliKayit.evrak_otomasyon_notu || "—"}
                                        </p>
                                    </div>
                                </div>

                                <div className="wa-onay-drawer__form">
                                    <label className="text-xs text-[#8696a0]">Dosya değiştir (no / plaka)</label>
                                    <input
                                        type="search"
                                        value={dosyaArama}
                                        onChange={async (e) => {
                                            const q = e.target.value;
                                            setDosyaArama(q);
                                            if (q.trim().length < 2) {
                                                setDosyaAdaylar([]);
                                                return;
                                            }
                                            try {
                                                const res = await crud.list("sigorta-dosyalari", {
                                                    search: q.trim(),
                                                    page_size: 8,
                                                });
                                                setDosyaAdaylar(res.results || res || []);
                                            } catch {
                                                setDosyaAdaylar([]);
                                            }
                                        }}
                                        placeholder="MBS-… veya plaka"
                                    />
                                    {dosyaAdaylar.length > 0 && (
                                        <select
                                            value={seciliDosyaId}
                                            onChange={(e) => setSeciliDosyaId(e.target.value)}
                                        >
                                            <option value="">Önerilen dosya</option>
                                            {dosyaAdaylar.map((d) => (
                                                <option key={d.id} value={d.id}>
                                                    {(d.dosya_no || `#${d.id}`) +
                                                        (d.plaka ? ` · ${d.plaka}` : "") +
                                                        (d.muvekkil_ad_soyad ? ` · ${d.muvekkil_ad_soyad}` : "")}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <label className="flex items-center gap-2 text-xs text-[#8696a0] cursor-pointer mt-2">
                                        <Checkbox checked={konusmaKaydet} onCheckedChange={(v) => setKonusmaKaydet(!!v)} />
                                        Bu konuşmaya varsayılan dosya kaydet
                                    </label>
                                    {konusmaKaydet && (
                                        <label className="flex items-center gap-2 text-xs text-[#8696a0] cursor-pointer">
                                            <Checkbox
                                                checked={herZamanBuDosya}
                                                onCheckedChange={(v) => setHerZamanBuDosya(!!v)}
                                            />
                                            Bu numaradan gelen tüm evrakları bu dosyaya kaydet
                                        </label>
                                    )}
                                </div>

                                <div className="wa-onay-drawer__actions">
                                    <Button
                                        variant="outline"
                                        className="border-white/20 text-slate-300"
                                        disabled={busy}
                                        onClick={handleReddet}
                                    >
                                        <XCircle className="h-4 w-4 mr-1" />
                                        Reddet
                                    </Button>
                                    <Button
                                        className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1"
                                        disabled={busy || !hedefDosyaId}
                                        onClick={handleOnayla}
                                    >
                                        {busy ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                        ) : (
                                            <Check className="h-4 w-4 mr-1" />
                                        )}
                                        Onayla ve yükle
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </aside>
        </div>,
        document.body,
    );
}
